import {
  action,
  computed,
  observable,
  runInAction,
  makeAutoObservable,
} from 'mobx';
import { Notifier } from 'common/services/notification.service';
import { AccountService } from 'common/services/account.service';
import { captureError, NotFoundAccountError } from 'common/utils/error.util';
import history from 'customHistory';
import i18n from 'i18n';
import ProfileStore from '../profile.store';
import { AccountUtils } from '../../utils/account.util';
import { _ExnessAccount } from './exness-account.store';

class BackEndAccounts {
  _accountListReady = [];

  _accountListInProgress = [];

  _accountListArchived = [];

  _loading = false;

  _servers = [];

  masterAccount = [];

  accountsDemoReal = [];

  accountsReady = false;

  isFetching = false;

  getAccounts = async () => {
    if (this._accountListReady.length !== 0) return this._accountListReady;
    return this.fetchAccounts({ needUpdate: true });
  };

  fetchAccounts = async (options = {}, returnCallback) => {
    this._loading = true;
    const self = this;
    this.isFetching = true;
    const { needUpdate } = options;

    try {
      let { accountsDemoReal } = this;
      if (!this.accountsReady || needUpdate) {
        const resp = await AccountService.getAccounts();

        const rawAccounts = await Promise.all(
          resp.map((account) =>
            Promise.all([
              AccountService.getAccount({
                id: account.external_id,
                type: account.account_type,
              }),
              AccountService.getAccountFinDetails({
                id: account.external_id,
                type: account.account_type,
              }),
              AccountService.getAccountId({
                id: account.external_id,
                type: account.account_type,
              }),
            ])
              .then(([acc, finDetails, accId]) => {
                let res = {};
                if (finDetails && acc && accId) {
                  res = {
                    ...res,
                    ...finDetails,
                    ...acc,
                    ...accId,
                  };

                  res = AccountUtils.transformv2AccountToV1(
                    res,
                    account.account_type
                  );
                  return res;
                }
                return null;
              })
              .catch((err) => {
                const notFound =
                  err?.message && err.message.indexOf('404 Not Found') > -1;

                if (notFound) {
                  AccountService.deleteAccount({
                    type: account.account_type,
                    accountId: account.external_id,
                  });
                } else {
                  captureError(
                    new NotFoundAccountError(
                      `Error getting info for account ${account.external_id}`
                    )
                  );
                  captureError(err);
                }

                return false;
              })
          )
        ).then((data) => data.filter((el) => el));

        accountsDemoReal = rawAccounts.map((el) => ({
          ...el,
          server_info: {
            ...el.server_info,
            provider: this.accountTypeToServerId[el.account_type],
          },
        }));
        this.accountsReady = true;
      }
      this.accountsDemoReal = [...accountsDemoReal];
      const data = [...accountsDemoReal];
      self.isFetching = false;
      if (options.needClear) {
        runInAction(() => {
          this._accountListReady = [];
        });
      }
      if (!data) {
        return;
      }

      const addAccount = (accounts, accountData, lastItem) => {
        if (
          !accounts.find(
            (account) => account.accountNumber === accountData.account
          )
        ) {
          if (lastItem) {
            accounts.push(
              new _ExnessAccount(accountData, options, () => {
                this._loading = false;
              })
            );
          } else {
            accounts.push(new _ExnessAccount(accountData, options));
          }
        }
      };
      runInAction(() => {
        this._accountListInProgress = [];
      });

      data.forEach((item, index) => {
        if (!item.account) {
          runInAction(() => {
            this._accountListInProgress.push(item);
          });
          setTimeout(() => self.fetchAccounts(options), 3000);
          return;
        }

        if (item.archived) {
          addAccount(
            this._accountListArchived,
            item,
            index === data.length - 1
          );
        } else {
          runInAction(() => {
            addAccount(this._accountListReady, item, index === data.length - 1);
          });
        }
      });
      returnCallback?.(data);

      return self._accountListReady;
    } catch (error) {
      captureError(error);
      this._loading = false;
    }
  };

  constructor() {
    makeAutoObservable(this);
  }

  get hasMasterAccount() {
    return !!this.readyAccounts.some((acc) => acc.isMaster);
  }

  get hasInvestorAccount() {
    return !!this.readyAccounts.some((acc) => acc.isInvestor);
  }

  get loading() {
    return this._loading;
  }

  get readyAccounts() {
    return this._accountListReady
      .slice()
      .sort((a, b) => b._account.role.localeCompare(a._account.role));
  }

  get hasReadyRealAccount() {
    const list = this._accountListReady;
    return list.filter((el) => el.isTrading).length !== 0;
  }

  get pendingAccounts() {
    return this._accountListInProgress;
  }

  get getMasterAccounts() {
    return this.accountsDemoReal.filter(
      (acc) => acc.account_type_info.account_type === 'master_real'
    );
  }

  get getMasterAccount() {
    return this.getMasterAccounts?.[0];
  }

  get getInvestorAccounts() {
    return this.accountsDemoReal.filter(
      (acc) => acc.account_type_info.account_type === 'investor_real'
    );
  }

  get getInvestorAccountsFromListReady() {
    return this._accountListReady.filter(
      (acc) =>
        acc.accountInfo.account_type_info.account_type === 'investor_real'
    );
  }

  get getInvestorAccountReady() {
    return this.getInvestorAccountsFromListReady?.[0];
  }

  get getInvestorAccount() {
    return this.getInvestorAccounts?.[0];
  }

  get servers() {
    return this.readyAccounts
      .slice()
      .reduce((acc, account) => {
        const { serverInfo } = account;

        const server = {
          type: serverInfo.type,
          name: serverInfo.name,
          hasStreaming: serverInfo.has_streaming,
        };

        if (
          !acc.find(
            (findServer) =>
              findServer.type === server.type && findServer.name === server.name
          )
        ) {
          acc.push(server);
        }

        return acc;
      }, [])
      .sort((a, b) => a?.name?.localeCompare(b?.name));
  }

  get accountTypeToServerId() {
    return Object.fromEntries(
      this._servers.map((el) => [el.account_type_key, el.provider])
    );
  }

  getServers = async () => {
    try {
      if (this._servers.length === 0) {
        this._servers = await AccountService.getServers();
      }
      return this._servers;
    } catch (e) {
      captureError(e);
      Notifier.error(
        i18n.t(
          'common.notification.getServers.error',
          'Could not get servers list'
        )
      );
      return [];
    }
  };

  setAccountNameToLocalList = ({ accId, name }) => {
    this._accountListReady.forEach((acc) => {
      const account = acc;
      if (account._account.account_id === accId) {
        account._account.name = name;
      }
    });

    [
      ...this.accountsDemoReal,
      this.masterAccounts,
      this.investorAccounts,
    ].forEach((acc) => {
      const account = acc;
      if (account?.account_id === accId) {
        account.name = name;
      }
    });
  };

  saveEditMasterAccount = async (payload) => {
    try {
      this.isFetching = true;
      await AccountService.saveEditMasterAccount(
        payload,
        this.getMasterAccount.account_id
      );
      await this.fetchAccounts({ needUpdate: true });
      Notifier.success(
        i18n.t(
          'common.notification.accountEdit.success',
          'Your account settings updated successfully'
        )
      );
    } catch (e) {
      captureError(e);
      Notifier.error(
        i18n.t(
          'common.notification.accountEdit.error',
          'Could not update account setting'
        )
      );
    } finally {
      this.isFetching = false;
    }
  };

  makeMasterAccount = async (account) => {
    try {
      if (!this.isFetching) {
        this.isFetching = true;
        await AccountService.createMasterAccount(account);
        history.push('/accounts?update=true');
        Notifier.success(
          i18n.t(
            'common.notification.masterAccountCreation.success',
            'Successfully created new Master account'
          )
        );
      }
    } catch (e) {
      captureError(e);
      Notifier.error(String(e));
    } finally {
      this.isFetching = false;
    }
  };

  makeInvestorAccount = async (account) => {
    try {
      const id = await AccountService.createInvestorAccount(account);
      history.push(`/operations?id=${id}`);
      Notifier.success(
        i18n.t(
          'common.notification.investorAccountCreation.success',
          'Successfully created new Investor account'
        )
      );
    } catch (e) {
      captureError(e);
      Notifier.error(
        i18n.t(
          'common.notification.investorAccountCreation.error',
          'Wrong Master ID, please try again with valid ID'
        )
      );
    }
  };

  makeInvestorAccountInWizard = async (account) => {
    try {
      await AccountService.createInvestorAccount(account);
      await ProfileStore.fetchProfileData({ needUpdate: true });
    } catch (e) {
      captureError(e);
      Notifier.error(String(e));
    }
  };

  saveEditInvestorAccount = async (payload) => {
    try {
      this.isFetching = true;
      await AccountService.saveEditInvestorAccount(
        payload,
        this.getInvestorAccount.account_id
      );
      await this.fetchAccounts({ needUpdate: true });
      Notifier.success(
        i18n.t(
          'common.notification.accountEdit.success',
          'Your account settings updated successfully'
        )
      );
    } catch (e) {
      captureError(e);
      Notifier.error(
        i18n.t(
          'common.notification.accountEdit.error',
          'Could not update account setting'
        )
      );
    } finally {
      this.isFetching = false;
    }
  };
}

const AccountsStore = new BackEndAccounts();
export default AccountsStore;
